import * as React from 'react';
import styled from 'styled-components';
import { colors } from '../theme';

// Reusable HUD-style panel: hairline border + quiet corner brackets, used to
// give cards / stat readouts / the hero a "mission control instrument" feel
// without competing with the painterly art behind it.
export const HudPanel = styled.div`
  position: relative;
  border: 1px solid ${colors.hairline};
  background: rgba(10, 14, 26, 0.55);
  backdrop-filter: blur(2px);

  &::before,
  &::after,
  & .corner-tl,
  & .corner-br {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    pointer-events: none;
    opacity: 0.85;
  }

  &::before {
    top: -1px;
    left: -1px;
    border-top: 1px solid ${colors.amber};
    border-left: 1px solid ${colors.amber};
  }

  &::after {
    bottom: -1px;
    right: -1px;
    border-bottom: 1px solid ${colors.amber};
    border-right: 1px solid ${colors.amber};
  }
`;

// The 4-point diffraction-spike star-flare shape recurring across every
// source painting — used as a small UI motif (bullets, hover glints, status
// marks) so the site inherits the paintings' own visual signature.
export const StarFlare = styled.svg`
  width: 1em;
  height: 1em;
  flex-shrink: 0;
  display: inline-block;
`;

export function StarFlareIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <StarFlare className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C12.4 5.2 13.6 8.4 16 10.5C18.4 12.6 19.8 13 24 12C19.8 13 18.4 13.4 16 15.5C13.6 17.6 12.4 20.8 12 24C11.6 20.8 10.4 17.6 8 15.5C5.6 13.4 4.2 13 0 12C4.2 13 5.6 12.6 8 10.5C10.4 8.4 11.6 5.2 12 0Z" />
    </StarFlare>
  );
}

export const Eyebrow = styled.p`
  font-family: 'JetBrains Mono', 'Space Mono', monospace;
  font-size: 0.7rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${colors.cyan};
  opacity: 0.8;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;

  @media (max-width: 768px) {
    font-size: 0.64rem;
    letter-spacing: 0.16em;
  }
`;

export const StatusDot = styled.span<{ $color?: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${props => props.$color || colors.ok};
  box-shadow: 0 0 6px ${props => props.$color || colors.ok};
  flex-shrink: 0;
  animation: blink 2.4s ease-in-out infinite;

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.25; }
  }
`;

export const GridOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(${colors.hairline} 1px, transparent 1px),
    linear-gradient(90deg, ${colors.hairline} 1px, transparent 1px);
  background-size: 42px 42px;
  opacity: 0.16;
  mask-image: radial-gradient(ellipse at center, black 0%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 0%, transparent 75%);
`;

// Full-bleed, gradient-masked backdrop used to seat a painting behind a
// content section — dark enough at the edges/bottom to keep HUD text legible
// while still letting the art read as the dominant visual.
export const SectionBackdrop = styled.div<{ $image: string }>`
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image:
    linear-gradient(180deg, rgba(5,7,13,0.55) 0%, rgba(5,7,13,0.86) 60%, rgba(5,7,13,0.98) 100%),
    url(${props => props.$image});
  background-size: cover;
  background-position: center;
  opacity: 0.9;
`;

// Primary CTA — amber/chrome HUD treatment consistent with the site's
// existing accent language (amber border + glow on hover, same family as
// the nav emblem's hover glow and HudPanel's amber corner brackets). Shared
// between the hero (full size) and the nav bar ($compact, tighter padding/
// font-size to fit the sticky bar without pushing its height).
export const ApplyButton = styled.a<{ $compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.$compact ? '0.4rem' : '0.6rem'};
  font-family: 'JetBrains Mono', 'Space Mono', monospace;
  font-size: ${props => props.$compact ? '0.68rem' : '0.82rem'};
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-decoration: none;
  color: ${colors.bgDeep};
  background: linear-gradient(120deg, ${colors.amber} 0%, #ffd27a 100%);
  border: 1px solid ${colors.amber};
  padding: ${props => props.$compact ? '0.5rem 0.9rem' : '0.85rem 1.6rem'};
  border-radius: 2px;
  box-shadow: 0 0 0 rgba(242, 169, 59, 0);
  transition: box-shadow 0.3s ease, transform 0.25s ease, background 0.3s ease;
  white-space: nowrap;

  &:hover {
    box-shadow: 0 0 24px rgba(242, 169, 59, 0.55);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: ${props => props.$compact ? '0.62rem' : '0.72rem'};
    padding: ${props => props.$compact ? '0.45rem 0.75rem' : '0.7rem 1.3rem'};
  }
`;
